/**
 * Comprehensive loading state management hook
 * Provides loading states for different operations with debouncing and caching
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface LoadingState {
  isLoading: boolean;
  error: string | null;
  progress?: number;
  stage?: string;
}

export interface LoadingStates {
  [key: string]: LoadingState;
}

export interface UseLoadingStatesOptions {
  debounceMs?: number;
  maxRetries?: number;
  retryDelay?: number;
}

export function useLoadingStates(
  initialState: LoadingStates = {},
  options: UseLoadingStatesOptions = {}
) {
  const {
    debounceMs = 300,
    maxRetries = 3,
    retryDelay = 1000
  } = options;

  const [states, setStates] = useState<LoadingStates>(initialState);
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const retryCounts = useRef<Map<string, number>>(new Map());

  /**
   * Set loading state for a specific operation
   */
  const setLoading = useCallback((
    operation: string,
    isLoading: boolean,
    error: string | null = null,
    progress?: number,
    stage?: string
  ) => {
    // Clear existing debounce timer
    const existingTimer = debounceTimers.current.get(operation);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Debounce loading state changes
    const timer = setTimeout(() => {
      setStates(prev => ({
        ...prev,
        [operation]: {
          isLoading,
          error,
          progress,
          stage
        }
      }));

      // Clear retry count on success
      if (!isLoading && !error) {
        retryCounts.current.delete(operation);
      }
    }, debounceMs);

    debounceTimers.current.set(operation, timer);
  }, [debounceMs]);

  /**
   * Set error state for an operation
   */
  const setError = useCallback((operation: string, error: string) => {
    setLoading(operation, false, error);
  }, [setLoading]);

  /**
   * Set success state for an operation
   */
  const setSuccess = useCallback((operation: string) => {
    setLoading(operation, false, null);
  }, [setLoading]);

  /**
   * Set progress for an operation
   */
  const setProgress = useCallback((
    operation: string,
    progress: number,
    stage?: string
  ) => {
    setStates(prev => ({
      ...prev,
      [operation]: {
        ...prev[operation],
        progress,
        stage
      }
    }));
  }, []);

  /**
   * Execute an async operation with loading state management
   */
  const execute = useCallback(async <T>(
    operation: string,
    asyncFn: () => Promise<T>,
    options: {
      retryOnError?: boolean;
      progressCallback?: (progress: number, stage?: string) => void;
    } = {}
  ): Promise<T> => {
    const { retryOnError = false, progressCallback } = options;
    
    try {
      setLoading(operation, true);
      
      if (progressCallback) {
        progressCallback(0, 'Starting...');
      }

      const result = await asyncFn();
      
      setSuccess(operation);
      
      if (progressCallback) {
        progressCallback(100, 'Completed');
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Handle retries
      if (retryOnError) {
        const currentRetries = retryCounts.current.get(operation) || 0;
        
        if (currentRetries < maxRetries) {
          retryCounts.current.set(operation, currentRetries + 1);
          
          setLoading(operation, true, null, undefined, `Retrying... (${currentRetries + 1}/${maxRetries})`);
          
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          
          return execute(operation, asyncFn, options);
        }
      }
      
      setError(operation, errorMessage);
      throw error;
    }
  }, [setLoading, setSuccess, setError, maxRetries, retryDelay]);

  /**
   * Check if any operation is loading
   */
  const isAnyLoading = useCallback(() => {
    return Object.values(states).some(state => state.isLoading);
  }, [states]);

  /**
   * Check if a specific operation is loading
   */
  const isLoading = useCallback((operation: string) => {
    return states[operation]?.isLoading || false;
  }, [states]);

  /**
   * Get error for a specific operation
   */
  const getError = useCallback((operation: string) => {
    return states[operation]?.error || null;
  }, [states]);

  /**
   * Get progress for a specific operation
   */
  const getProgress = useCallback((operation: string) => {
    return states[operation]?.progress || 0;
  }, [states]);

  /**
   * Get stage for a specific operation
   */
  const getStage = useCallback((operation: string) => {
    return states[operation]?.stage || '';
  }, [states]);

  /**
   * Clear all loading states
   */
  const clearAll = useCallback(() => {
    setStates({});
    debounceTimers.current.clear();
    retryCounts.current.clear();
  }, []);

  /**
   * Clear a specific loading state
   */
  const clear = useCallback((operation: string) => {
    setStates(prev => {
      const newStates = { ...prev };
      delete newStates[operation];
      return newStates;
    });
    
    const timer = debounceTimers.current.get(operation);
    if (timer) {
      clearTimeout(timer);
      debounceTimers.current.delete(operation);
    }
    
    retryCounts.current.delete(operation);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      debounceTimers.current.forEach(timer => clearTimeout(timer));
      debounceTimers.current.clear();
    };
  }, []);

  return {
    states,
    setLoading,
    setError,
    setSuccess,
    setProgress,
    execute,
    isLoading,
    isAnyLoading,
    getError,
    getProgress,
    getStage,
    clear,
    clearAll
  };
}

/**
 * Hook for managing form submission loading states
 */
export function useFormLoading() {
  const { states, setLoading, setError, setSuccess, isLoading, getError } = useLoadingStates();

  const submitForm = useCallback(async <T>(
    operation: string,
    submitFn: () => Promise<T>
  ): Promise<T> => {
    try {
      setLoading(operation, true);
      const result = await submitFn();
      setSuccess(operation);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Submission failed';
      setError(operation, errorMessage);
      throw error;
    }
  }, [setLoading, setSuccess, setError]);

  return {
    submitForm,
    isLoading: isLoading('submit'),
    error: getError('submit')
  };
}

/**
 * Hook for managing API call loading states
 */
export function useApiLoading() {
  const { states, setLoading, setError, setSuccess, isLoading, getError, execute } = useLoadingStates();

  const callApi = useCallback(async <T>(
    operation: string,
    apiCall: () => Promise<T>,
    options: {
      retryOnError?: boolean;
      showProgress?: boolean;
    } = {}
  ): Promise<T> => {
    const { retryOnError = true, showProgress = false } = options;

    return execute(operation, apiCall, {
      retryOnError,
      progressCallback: showProgress ? (progress, stage) => {
        if (showProgress) {
          setLoading(operation, true, null, progress, stage);
        }
      } : undefined
    });
  }, [execute, setLoading]);

  return {
    callApi,
    isLoading: (operation: string) => isLoading(operation),
    getError,
    states
  };
}

export default useLoadingStates;
